import { Link } from "@tanstack/react-router";
import { cn } from "@/lib/utils";
import {
  FileText,
  UserCheck,
  CreditCard,
  Shield,
  Brain,
  Lock,
  Trash2,
  Globe,
  Mail,
} from "lucide-react";

const LAST_UPDATED = "4 de junho de 2026";
const CONTACT_EMAIL = "suporte@lucralink.com.br";

/**
 * Termos de Uso publicos (sem login).
 * Servida em https://lucralink.com.br/termos.
 */
export function TermosPage(): JSX.Element {
  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      {/* ---- Header ---- */}
      <header className="sticky top-0 z-50 border-b border-border/60 bg-background/80 backdrop-blur-md">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="font-bold tracking-tight text-lg text-foreground">
            Lucra<span className="text-primary">Link</span>
          </Link>
          <Link
            to="/"
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            Voltar ao início
          </Link>
        </div>
      </header>

      <main className="flex-1">
        <article className="max-w-3xl mx-auto px-4 sm:px-6 py-16 space-y-12">
          <TermsIntro />

          {/* ---- 1. Definicoes ---- */}
          <Section icon={FileText} title="1. Definições">
            <p>
              Para os fins destes Termos de Uso, as expressões abaixo têm o seguinte
              significado:
            </p>
            <ul className="space-y-2.5 pl-1">
              <Item>
                <strong className="text-foreground">LucraLink</strong> é o nome fantasia do
                serviço de automação de links de afiliado operado por Nikolas de Hor,
                pessoa física inscrita no CPF sob o número 521.384.628-09, doravante
                denominado simplesmente &ldquo;Plataforma&rdquo;.
              </Item>
              <Item>
                <strong className="text-foreground">Usuário</strong> é toda pessoa física
                maior de 18 anos que se cadastre e utilize a Plataforma, seja na condição
                de titular de conta gratuita (Free) ou pagante (PRO).
              </Item>
              <Item>
                <strong className="text-foreground">Plataformas</strong> são os marketplaces
                e redes de afiliados suportados pelo LucraLink, incluindo, mas não se
                limitando a: Mercado Livre, Amazon Brasil, Shopee, AliExpress, Magalu e
                AWIN (SHEIN).
              </Item>
              <Item>
                <strong className="text-foreground">Links de afiliado</strong> são URLs
                geradas pelo LucraLink que contêm o identificador único de afiliado do
                Usuário, permitindo o rastreio de conversões e o pagamento de comissões
                pelas Plataformas.
              </Item>
            </ul>
          </Section>

          {/* ---- 2. Cadastro e conta ---- */}
          <Section icon={UserCheck} title="2. Cadastro e conta">
            <p>
              O cadastro no LucraLink é gratuito e pode ser realizado diretamente no site
              ou via autenticação com Google. Ao se cadastrar, o Usuário declara:
            </p>
            <ul className="space-y-2.5 pl-1">
              <Item>
                Ser maior de 18 anos e ter plena capacidade civil para contratar.
              </Item>
              <Item>
                Fornecer dados cadastrais verdadeiros, atualizados e completos.
              </Item>
              <Item>
                Ser o legítimo titular da conta de afiliado que pretende conectar em cada
                Plataforma suportada.
              </Item>
              <Item>
                Manter o sigilo das suas credenciais de acesso ao LucraLink,
                responsabilizando-se integralmente pelo uso que terceiros fizerem da sua
                conta, salvo na hipótese de comunicação imediata de uso não autorizado.
              </Item>
            </ul>
            <p>
              O LucraLink se reserva o direito de recusar, suspender ou cancelar cadastros
              a seu exclusivo critério, sem necessidade de justificativa, especialmente em
              casos de suspeita de fraude, uso indevido ou descumprimento destes Termos.
            </p>
          </Section>

          {/* ---- 3. Planos e pagamento ---- */}
          <Section icon={CreditCard} title="3. Planos e pagamento">
            <p>
              O LucraLink oferece duas modalidades de conta:
            </p>
            <ul className="space-y-2.5 pl-1">
              <Item>
                <strong className="text-foreground">Plano Free (gratuito):</strong> inclui
                funcionalidades básicas limitadas, conforme descrito na página de planos da
                Plataforma. Não há cobrança de qualquer valor.
              </Item>
              <Item>
                <strong className="text-foreground">Plano PRO (pago):</strong> desbloqueia
                funcionalidades avançadas e limites maiores, com cobrança recorrente mensal
                ou anual processada exclusivamente pela{" "}
                <strong className="text-foreground">Stripe</strong>, uma plataforma de
                pagamentos internacional. O LucraLink não armazena, transmite ou tem acesso
                aos dados de cartão de crédito do Usuário.
              </Item>
            </ul>
            <p>
              <strong className="text-foreground">Reembolso:</strong> em conformidade com o
              CDC (art. 49), o Usuário pode solicitar o cancelamento e reembolso integral
              em até 7 dias corridos da contratação, desde que não tenha utilizado
              efetivamente os recursos premium de forma significativa. Após esse período,
              não haverá reembolso de mensalidades já pagas, podendo o Usuário cancelar a
              assinatura a qualquer momento para evitar novas cobranças.
            </p>
            <p>
              Valores, periodicidade e benefícios de cada plano podem ser alterados
              mediante comunicação prévia aos Usuários ativos com no mínimo 30 dias de
              antecedência.
            </p>
          </Section>

          {/* ---- 4. Obrigacoes do usuario ---- */}
          <Section icon={Shield} title="4. Obrigações do usuário">
            <p>
              Ao utilizar o LucraLink, o Usuário se compromete a:
            </p>
            <ul className="space-y-2.5 pl-1">
              <Item>
                Utilizar a Plataforma exclusivamente para gerar links de afiliado em nome
                da sua própria conta, vinculada a plataformas de terceiros.
              </Item>
              <Item>
                Não utilizar o LucraLink para envio de spam, mensagens não solicitadas ou
                qualquer prática de marketing abusiva.
              </Item>
              <Item>
                Não criar múltiplas contas para burlar limites do plano Free ou qualquer
                restrição da Plataforma.
              </Item>
              <Item>
                Não utilizar contas de afiliado de terceiros sem autorização expressa.
              </Item>
              <Item>
                Manter-se em conformidade com os Termos de Uso e políticas de cada
                Plataforma parceira, isentando o LucraLink de qualquer responsabilidade
                por violações cometidas pelo Usuário.
              </Item>
              <Item>
                Não reproduzir, modificar, distribuir, descompilar ou engenheirar
                reversamente a Plataforma ou qualquer de seus componentes sem autorização
                prévia por escrito.
              </Item>
            </ul>
          </Section>

          {/* ---- 5. Propriedade intelectual ---- */}
          <Section icon={Brain} title="5. Propriedade intelectual">
            <p>
              Todo o conteúdo, código-fonte, design, logotipos, marcas, nome fantasia e
              demais elementos intelectuais do LucraLink são de propriedade exclusiva do
              operador, protegidos pela legislação brasileira de direitos autorais (Lei
              9.610/98) e de propriedade industrial (Lei 9.279/96).
            </p>
            <p>
              Nenhum elemento da Plataforma pode ser copiado, reproduzido, redistribuído ou
              utilizado para fins comerciais sem autorização expressa e por escrito do
              operador. O Usuário concede ao LucraLink uma licença limitada, não exclusiva
              e revogável para processar os dados necessários à geração dos links de
              afiliado, exclusivamente no âmbito da prestação do serviço contratado.
            </p>
          </Section>

          {/* ---- 6. Dados e privacidade ---- */}
          <Section icon={Lock} title="6. Dados e privacidade">
            <p>
              O tratamento de dados pessoais dos Usuários é realizado em conformidade com a
              Lei Geral de Proteção de Dados Pessoais (LGPD - Lei 13.709/18). As
              informações sobre quais dados coletamos, para que finalidades, como
              armazenamos e como você pode exercer seus direitos estão detalhadas na nossa{" "}
              <Link
                to="/privacidade"
                className="text-primary underline-offset-4 hover:underline"
              >
                Política de Privacidade
              </Link>
              .
            </p>
            <p>
              Nos termos do art. 18 da LGPD, o Usuário titular dos dados tem o direito de
              obter do LucraLink, a qualquer momento:
            </p>
            <ul className="space-y-2.5 pl-1">
              <Item>
                Confirmação da existência de tratamento de dados pessoais.
              </Item>
              <Item>
                Acesso aos dados tratados.
              </Item>
              <Item>
                Correção de dados incompletos, inexatos ou desatualizados.
              </Item>
              <Item>
                Anonimização, bloqueio ou eliminação de dados desnecessários, excessivos ou
                tratados em desconformidade com a LGPD.
              </Item>
              <Item>
                Portabilidade dos dados a outro fornecedor de serviço, mediante requisição
                expressa.
              </Item>
              <Item>
                Eliminação dos dados tratados com o consentimento do titular.
              </Item>
              <Item>
                Informação sobre o compartilhamento de dados com entes públicos e privados.
              </Item>
              <Item>
                Revisão de decisões automatizadas, quando aplicável.
              </Item>
            </ul>
          </Section>

          {/* ---- 7. Exclusao de conta ---- */}
          <Section icon={Trash2} title="7. Exclusão de conta">
            <p>
              O Usuário pode, a qualquer momento, solicitar a exclusão definitiva da sua
              conta e de todos os dados associados. Para isso, basta:
            </p>
            <ul className="space-y-2.5 pl-1">
              <Item>
                Acessar a área{" "}
                <strong className="text-foreground">Minha Conta</strong> no painel do
                LucraLink e utilizar a opção de exclusão de conta.
              </Item>
              <Item>
                Ou enviar uma solicitação para{" "}
                <a
                  href={`mailto:${CONTACT_EMAIL}`}
                  className="text-primary underline-offset-4 hover:underline"
                >
                  {CONTACT_EMAIL}
                </a>
                .
              </Item>
            </ul>
            <p>
              A exclusão resulta na remoção permanente de todos os dados pessoais,
              configurações de sessão (incluindo cookies de Plataformas armazenados no
              Vault), grupos cadastrados e histórico de conversões. O LucraLink concluirá
              o processo em até 15 dias úteis, nos termos do art. 18, VI, da LGPD.
            </p>
            <p>
              Dados que o LucraLink seja obrigado a manter por força de lei (por exemplo,
              registros de transações financeiras para cumprimento de obrigação fiscal)
              serão retidos pelo prazo legal aplicável e posteriormente eliminados.
            </p>
          </Section>

          {/* ---- 8. Disposicoes gerais ---- */}
          <Section icon={Globe} title="8. Disposições gerais">
            <p>
              <strong className="text-foreground">Lei aplicável:</strong> estes Termos de
              Uso são regidos pela legislação brasileira, em especial o Código de Defesa do
              Consumidor (CDC - Lei 8.078/90) e o Marco Civil da Internet (Lei 12.965/14).
            </p>
            <p>
              <strong className="text-foreground">Foro:</strong> fica eleito o foro da
              Comarca de São Paulo, Estado de São Paulo, para dirimir quaisquer controvérsias
              oriundas destes Termos, com renúncia expressa a qualquer outro, por mais
              privilegiado que seja.
            </p>
            <p>
              <strong className="text-foreground">Alterações:</strong> o LucraLink poderá
              alterar estes Termos de Uso a qualquer momento, comunicando os Usuários com
              no mínimo 15 dias de antecedência por meio do e-mail cadastrado ou de aviso
              na própria Plataforma. O uso continuado do serviço após a vigência das
              alterações implica aceitação dos novos termos.
            </p>
            <p>
              <strong className="text-foreground">Tolerância:</strong> o não exercício de
              qualquer direito previsto nestes Termos não constitui renúncia, podendo ser
              exercido a qualquer tempo.
            </p>
            <p>
              <strong className="text-foreground">Comunicações:</strong> todas as
              comunicações formais entre as partes serão feitas por escrito, preferencialmente
              por e-mail, sendo considerado válido o envio para o endereço eletrônico
              cadastrado pelo Usuário.
            </p>
          </Section>

          {/* ---- 9. Contato ---- */}
          <Section icon={Mail} title="9. Contato">
            <p>
              Em caso de dúvidas sobre estes Termos de Uso, o Usuário pode entrar em contato
              pelo e-mail{" "}
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="text-primary underline-offset-4 hover:underline"
              >
                {CONTACT_EMAIL}
              </a>
              .
            </p>
          </Section>
        </article>
      </main>

      {/* ---- Footer ---- */}
      <footer className="border-t border-border/40 py-8 px-4">
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-muted-foreground">
          <span className="font-bold tracking-tight text-sm text-muted-foreground">
            Lucra<span className="text-primary">Link</span>
          </span>
          <p>
            &copy; {new Date().getFullYear()} LucraLink. Todos os direitos reservados.
          </p>
          <Link to="/" className="hover:text-foreground transition-colors">
            Início
          </Link>
        </div>
      </footer>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-componentes
// ---------------------------------------------------------------------------

function TermsIntro(): JSX.Element {
  return (
    <>
      <header className="space-y-4">
        <span className="inline-flex items-center gap-2 rounded-full border border-[hsl(var(--gold)/0.3)] bg-[hsl(var(--gold)/0.08)] px-3 py-1 text-xs font-medium text-[hsl(var(--gold))]">
          <FileText size={13} />
          Termos de Uso
        </span>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">
          Termos de Uso
        </h1>
        <p className="text-sm text-muted-foreground">
          Plataforma LucraLink de automação de links de afiliado. Última
          atualização: {LAST_UPDATED}.
        </p>
      </header>

      <p className="text-base text-muted-foreground leading-relaxed">
        Estes Termos de Uso regulam a utilização da plataforma LucraLink por
        pessoas físicas maiores de 18 anos para a geração automatizada de links
        de afiliado em marketplaces parceiros. Ao criar uma conta e utilizar os
        serviços do LucraLink, o Usuário declara ter lido, compreendido e
        aceitado integralmente as condições abaixo. Caso discorde de qualquer
        disposição, o Usuário não deve utilizar a Plataforma.
      </p>
    </>
  );
}

interface SectionProps {
  icon: React.ElementType;
  title: string;
  children: React.ReactNode;
}

function Section({ icon: Icon, title, children }: SectionProps): JSX.Element {
  return (
    <section className="space-y-4">
      <h2 className="flex items-center gap-3 text-xl font-semibold tracking-tight">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
          <Icon size={17} className="text-primary" />
        </span>
        {title}
      </h2>
      <div
        className={cn(
          "space-y-4 text-sm leading-relaxed text-muted-foreground",
          "pl-12",
        )}
      >
        {children}
      </div>
    </section>
  );
}

function Item({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <li className="relative pl-5">
      <span className="absolute left-0 top-2 h-1.5 w-1.5 rounded-full bg-[hsl(var(--gold))]" />
      {children}
    </li>
  );
}
